/* CarGauge Task 13: additive accessibility/presentation layer for wishlist, compare and quick view.
   It does NOT replace theme.wishlist, theme.compare, the quick view AJAX loader or Bootstrap's modal.
   It creates no storage (the only storage access is a read-only getItem("items") to pick the wishlist
   empty/loading state), loads no library and adds no click handlers that call preventDefault or
   change cart/wishlist/compare data. Everything here is idempotent and only repairs generated markup. */
(function () {
  'use strict';

  var doc = document;
  function qs(sel, root) { return (root || doc).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function text(el) { return (el && el.textContent ? el.textContent : '').replace(/\s+/g, ' ').trim(); }
  function fill(template, token, value) { return (template || '').replace(token, value); }
  function observe(target, opts, fn) {
    if (!target || !window.MutationObserver) return null;
    var scheduled = false;
    var mo = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(function () { scheduled = false; fn(); }, 0);
    });
    mo.observe(target, opts);
    return mo;
  }

  /* ------------------------------------------------------------------ */
  /* Focus return for quick view and compare (Bootstrap 4.0 does not     */
  /* restore focus to the opener)                                         */
  /* ------------------------------------------------------------------ */
  var lastTrigger = null;
  var TRIGGER_SEL = '[data-quickview],[data-compare-selected]';

  doc.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest(TRIGGER_SEL) : null;
    if (t) lastTrigger = t;
  }, true);

  function restoreFocus() {
    var t = lastTrigger;
    lastTrigger = null;
    if (!t || !doc.body.contains(t)) return;
    try { t.focus({ preventScroll: true }); } catch (err) { t.focus(); }
    if (doc.activeElement !== t) {
      var card = t.closest ? t.closest('.product-card, .grid-item, .product-item') : null;
      var fallback = card ? qs('a[href], button', card) : null;
      if (fallback) fallback.focus();
    }
  }

  function bindFocusReturn() {
    var ids = ['product_quickview', 'compare'];
    if (window.jQuery) {
      window.jQuery(doc).on('hidden.bs.modal', '#product_quickview, #compare', restoreFocus);
      return;
    }
    ids.forEach(function (id) {
      var m = doc.getElementById(id);
      if (!m) return;
      var wasShown = false;
      observe(m, { attributes: true, attributeFilter: ['class', 'aria-hidden'] }, function () {
        var shown = m.classList.contains('show');
        if (wasShown && !shown) restoreFocus();
        wasShown = shown;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Wishlist page                                                       */
  /* ------------------------------------------------------------------ */
  function initWishlist() {
    var root = qs('[data-cg-wishlist]');
    if (!root) return;

    var tbody = qs('[data-wishlist-container]', root);
    var wrap = qs('[data-cg-wl-table-wrap]', root);
    var footer = qs('[data-cg-wl-footer]', root);
    var elLoading = qs('[data-cg-wl-loading]', root);
    var elEmpty = qs('[data-cg-wl-empty]', root);
    var elError = qs('[data-cg-wl-error]', root);
    var status = qs('[data-cg-wl-status]', root);
    var removeTpl = root.getAttribute('data-cg-remove-label') || '';
    var pageTpl = root.getAttribute('data-cg-page-label') || '';
    var priceLabel = (root.getAttribute('data-cg-col-labels') || '').split('|')[2] || '';
    var pagination = qs('#wishlist_pagination', root);
    var pagNav = pagination ? pagination.parentNode : null;
    if (!tbody) return;

    function storedHandles() {
      var list = [];
      try {
        var raw = window.localStorage.getItem('items'); /* read-only */
        var parsed = raw ? JSON.parse(raw) : [];
        if (Array.isArray(parsed)) list = parsed;
      } catch (err) { list = []; }
      var seen = {};
      return list.filter(function (h) { if (!h || seen[h]) return false; seen[h] = true; return true; });
    }

    var expected = storedHandles().length;
    var errorTimer = null;

    function show(el, on) { if (el) el.hidden = !on; }
    function say(msg) { if (status) status.textContent = msg || ''; }

    function rows() { return qsa('tr[data-wishlist-added]', tbody); }

    function repairRow(tr) {
      if (tr.getAttribute('data-cg-fixed') === '1') return;
      var title = text(qs('.product-title', tr)) || '';
      tr.setAttribute('role', 'row');

      qsa('.wishlist_col', tr).forEach(function (cell) {
        if (cell.classList.contains('wishlist_name')) {
          cell.setAttribute('role', 'rowheader');
          cell.setAttribute('scope', 'row');
        } else {
          cell.setAttribute('role', 'cell');
          cell.removeAttribute('scope');
        }
        if (cell.classList.contains('wishlist_price') && priceLabel) cell.setAttribute('data-cg-label', priceLabel);
      });

      /* generated image: alt can be the literal string "undefined" */
      var img = qs('.wishlist_image img', tr);
      if (img) {
        var alt = img.getAttribute('alt');
        if (alt === null || alt === 'undefined' || alt === 'null' || alt === '') {
          img.setAttribute('alt', '');
          var imgLink = img.closest('a');
          if (imgLink) {
            imgLink.setAttribute('aria-hidden', 'true');
            imgLink.setAttribute('tabindex', '-1');
          }
        }
      }

      /* remove control: <a href="#"> -> <button type="button">, same classes/data attributes */
      var rm = qs('.product-remove[data-icon-wishlist]', tr);
      if (rm && rm.tagName !== 'BUTTON') {
        var btn = doc.createElement('button');
        for (var i = 0; i < rm.attributes.length; i++) {
          var a = rm.attributes[i];
          if (a.name === 'href' || a.name === 'role') continue;
          btn.setAttribute(a.name, a.value);
        }
        btn.setAttribute('type', 'button');
        btn.innerHTML = rm.innerHTML;
        if (title && removeTpl) btn.setAttribute('aria-label', fill(removeTpl, '[product]', title));
        qsa('.icon', btn).forEach(function (ic) { ic.setAttribute('aria-hidden', 'true'); });
        rm.parentNode.replaceChild(btn, rm);
      }

      /* generated add-to-cart / select-options controls: product-specific names */
      qsa('.wishlist_add [data-btn-addToCart], .wishlist_add a.btn', tr).forEach(function (c) {
        var label = text(c);
        if (label && title && !c.hasAttribute('aria-label')) c.setAttribute('aria-label', label + ': ' + title);
      });
      var dis = qs('.wishlist_add button[disabled]', tr);
      if (dis && title && !dis.hasAttribute('aria-label')) dis.setAttribute('aria-label', text(dis) + ': ' + title);

      tr.setAttribute('data-cg-fixed', '1');
    }

    function repairPagination() {
      if (!pagination) return;
      qsa('a', pagination).forEach(function (a) {
        var page = a.getAttribute('data-page');
        if (page && pageTpl && !a.hasAttribute('aria-label')) a.setAttribute('aria-label', fill(pageTpl, '[page]', page));
        if (a.classList.contains('pagination--current')) a.setAttribute('aria-current', 'page');
        else a.removeAttribute('aria-current');
        if (a.classList.contains('disabled')) a.setAttribute('aria-disabled', 'true');
        else a.removeAttribute('aria-disabled');
      });
      if (pagNav) pagNav.hidden = !pagination.children.length;
    }

    function update() {
      var list = rows();
      list.forEach(repairRow);
      repairPagination();

      var stored = storedHandles().length;
      if (!list.length && stored === 0) {
        window.clearTimeout(errorTimer);
        show(elLoading, false); show(elError, false); show(elEmpty, true);
        show(wrap, false); show(footer, false);
        return;
      }
      show(elEmpty, false); show(wrap, true); show(footer, true);
      if (list.length) show(elLoading, false);
      if (list.length >= expected && expected > 0) {
        window.clearTimeout(errorTimer);
        show(elError, false);
      }
    }

    /* initial state */
    if (expected === 0) {
      update();
    } else {
      show(elLoading, true);
      say(elLoading ? text(elLoading) : '');
      errorTimer = window.setTimeout(function () {
        if (rows().length < expected) {
          show(elLoading, false);
          show(elError, true);
        }
      }, 10000);
    }

    /* keep focus sensible when the existing engine deletes a row */
    var removedIndex = -1;
    root.addEventListener('click', function (e) {
      var rm = e.target.closest ? e.target.closest('.product-remove[data-icon-wishlist]') : null;
      if (!rm) return;
      var tr = rm.closest('tr');
      var vis = rows().filter(function (r) { return r.style.display !== 'none'; });
      removedIndex = vis.indexOf(tr);
    }, true);

    observe(tbody, { childList: true }, function () {
      update();
      if (removedIndex > -1) {
        var vis = rows().filter(function (r) { return r.style.display !== 'none'; });
        var next = vis[Math.min(removedIndex, vis.length - 1)];
        var target = next ? qs('.product-title', next) : null;
        if (!target && elEmpty && !elEmpty.hidden) { elEmpty.setAttribute('tabindex', '-1'); target = elEmpty; }
        if (target) target.focus();
        removedIndex = -1;
      }
    });
    if (pagination) {
      observe(pagination, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] }, repairPagination);
    }
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Compare: checkbox state + generated modal repair                    */
  /* ------------------------------------------------------------------ */
  function syncCompareChecks() {
    qsa('[data-icon-compare]').forEach(function (box) {
      var input = qs('input.compare', box);
      if (!input) return;
      var on = box.classList.contains('compare-added');
      if (input.checked !== on) input.checked = on;
    });
  }

  function initCompareChecks() {
    if (!qs('[data-icon-compare]')) return;
    syncCompareChecks();
    observe(doc.body, { attributes: true, attributeFilter: ['class'], subtree: true }, syncCompareChecks);
    /* the engine calls preventDefault on the click, which makes the browser revert the native checkbox;
       re-assert the real state right after the event finishes */
    doc.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-icon-compare]')) window.setTimeout(syncCompareChecks, 0);
    });
  }

  function initCompareModal() {
    var modal = qs('[data-cg-compare-modal]');
    if (!modal) return;
    var wrapper = qs('[data-compare-modal]', modal);
    if (!wrapper) return;
    var notProvided = modal.getAttribute('data-cg-not-provided') || '';
    var removeTpl = modal.getAttribute('data-cg-remove-label') || '';
    var OPTIONAL_ROWS = ['.t-rating', '.t-description', '.t-collection', '.t-product-type', '.t-product-sku'];

    function isDash(s) { return s === '-' || s === '\u2013' || s === '\u2014'; }
    function cellEmpty(cell) {
      if (cell.hasAttribute('data-cg-empty')) return true;
      return text(cell) === '' && !qs('img, svg, input, button, a', cell);
    }

    function repair() {
      /* generated product cards: image alt + remove control */
      qsa('.product-grid [data-compare-added]', wrapper).forEach(function (card) {
        var title = text(qs('.product-title a', card));
        var img = qs('.product-image img', card);
        if (img) {
          var alt = img.getAttribute('alt');
          if (alt === null || alt === 'undefined' || alt === 'null' || alt === '') {
            img.setAttribute('alt', '');
            var l = img.closest('a');
            if (l) { l.setAttribute('aria-hidden', 'true'); l.setAttribute('tabindex', '-1'); }
          }
        }
        var rm = qs('a.product-remove[data-icon-compare-added]', card);
        if (rm) {
          var btn = doc.createElement('button');
          for (var i = 0; i < rm.attributes.length; i++) {
            var a = rm.attributes[i];
            if (a.name === 'href') continue;
            btn.setAttribute(a.name, a.value);
          }
          btn.setAttribute('type', 'button');
          btn.innerHTML = rm.innerHTML;
          if (title && removeTpl) btn.setAttribute('aria-label', fill(removeTpl, '[product]', title));
          rm.parentNode.replaceChild(btn, rm);
        } else {
          var existing = qs('button.product-remove[data-icon-compare-added]', card);
          if (existing && title && removeTpl && !existing.hasAttribute('aria-label')) {
            existing.setAttribute('aria-label', fill(removeTpl, '[product]', title));
          }
        }
        qsa('.product-btn[data-btn-addToCart], a.product-btn', card).forEach(function (c) {
          var label = text(c);
          if (label && title && !c.hasAttribute('aria-label')) c.setAttribute('aria-label', label + ': ' + title);
        });
      });

      /* fake dashes -> visually blank, readable "not provided" for assistive tech */
      OPTIONAL_ROWS.forEach(function (sel) {
        var row = qs('.product-row' + sel, wrapper);
        if (!row) return;
        var cells = qsa('.compare-content .grid > [data-compare-added]', row);
        cells.forEach(function (cell) {
          if (!cell.hasAttribute('data-cg-empty') && !qs('*', cell) && isDash(text(cell))) {
            cell.textContent = '';
            cell.setAttribute('data-cg-empty', '1');
          }
          if (cell.hasAttribute('data-cg-empty') && !qs('.visually-hidden', cell)) {
            var s = doc.createElement('span');
            s.className = 'visually-hidden';
            s.textContent = notProvided;
            cell.appendChild(s);
          }
        });
        var allEmpty = cells.every(function (c) {
          if (c.hasAttribute('data-cg-empty')) return true;
          return text(c) === '' && !qs('img, svg, input, button, a', c);
        });
        /* omit the whole row when every compared product lacks the value */
        row.hidden = allEmpty;
      });
    }

    observe(wrapper, { childList: true, subtree: true }, repair);
    repair();

    /* removing a product deletes the focused button; keep focus inside the dialog */
    modal.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-icon-compare-added]')) {
        window.setTimeout(function () {
          if (!modal.contains(doc.activeElement) || doc.activeElement === doc.body) modal.focus();
        }, 0);
      }
    }, true);
  }

  /* ------------------------------------------------------------------ */
  /* Quick view: verify the labelled heading survives AJAX injection     */
  /* ------------------------------------------------------------------ */
  function initQuickView() {
    var modal = doc.getElementById('product_quickview');
    if (!modal) return;
    var holder = qs('.product_template', modal);
    /* The modal is named by #cg-qv-title (rendered by templates/product.quickview.liquid). If the injected
       content ever lacks it, the static aria-label on the modal is the accessible name. */
    function repair() {
      var h = qs('#cg-qv-title', modal);
      if (h && modal.getAttribute('aria-labelledby') !== 'cg-qv-title') modal.setAttribute('aria-labelledby', 'cg-qv-title');
      qsa('.qty-group [data-qtt]', modal).forEach(function (b) {
        if (b.tagName === 'BUTTON' && !b.getAttribute('type')) b.setAttribute('type', 'button');
      });
    }
    observe(holder, { childList: true }, repair);
    repair();
  }

  function start() {
    bindFocusReturn();
    initWishlist();
    initCompareChecks();
    initCompareModal();
    initQuickView();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})();
